import { readServiceIds } from 'components/keywordMappings/keywordMappingModel'

export const KEYWORD_MAPPING_BULK_PREVIEW_URL = 'office/keyword-mappings/bulk-preview'
export const KEYWORD_MAPPING_BULK_UPDATE_URL = 'office/keyword-mappings/bulk-update'
export const KEYWORD_MAPPING_BULK_DELETE_URL = 'office/keyword-mappings/bulk-delete'

export type KeywordMappingBulkAction = 'h1' | 'activate' | 'deactivate' | 'delete'

export interface KeywordMappingListFilter {
    countries?: unknown
    serviceIds?: unknown
    keyword?: unknown
    searchIntent?: unknown
    active?: unknown
}

export interface KeywordMappingBulkFilter {
    countries?: string[]
    serviceIds?: number[]
    keyword?: string
    searchIntent?: string
    active?: '1' | '0'
}

export interface KeywordMappingIdsTarget {
    ids: number[]
}

export interface KeywordMappingFilterTarget {
    filter: KeywordMappingBulkFilter
}

export interface KeywordMappingGroupTarget {
    group: {
        serviceId: number
        searchIntent: string
    }
}

export type KeywordMappingBulkTarget =
    | KeywordMappingIdsTarget
    | KeywordMappingFilterTarget
    | KeywordMappingGroupTarget

export interface KeywordMappingBulkPreview {
    rowCount: number
    searchIntentCount: number
    h1RowCount: number
    outsideTargetCount: number
    maxRows: number
}

export interface KeywordMappingBulkWriteBody {
    target: KeywordMappingBulkTarget
    h1Text?: string
    active?: boolean
    confirmCount?: number
}

interface KeywordMappingBulkRequest {
    url: string
    body: KeywordMappingBulkWriteBody
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function readFilterStrings(value: unknown): string[] {
    if (!Array.isArray(value)) return []

    const items: string[] = []
    for (const item of value) {
        if (typeof item !== 'string') continue
        if (item === '') continue
        items.push(item)
    }
    return items
}

export function readAppliedFilter(
    filter: { keyword?: unknown; searchIntent?: unknown },
    field: 'keyword' | 'searchIntent',
): string {
    const value = filter[field]
    if (typeof value === 'string') return value
    return ''
}

export function buildKeywordMappingFilter(
    filter: KeywordMappingListFilter,
): KeywordMappingBulkFilter {
    const target: KeywordMappingBulkFilter = {}
    const countries = readFilterStrings(filter.countries)
    if (countries.length > 0) target.countries = countries

    const serviceIds = readServiceIds(filter.serviceIds)
    if (serviceIds.length > 0) target.serviceIds = serviceIds

    const keyword = readAppliedFilter(filter, 'keyword')
    if (keyword !== '') target.keyword = keyword

    const searchIntent = readAppliedFilter(filter, 'searchIntent')
    if (searchIntent !== '') target.searchIntent = searchIntent

    if (filter.active === '1' || filter.active === '0') target.active = filter.active

    return target
}

export function keywordMappingFilterSignature(filter: KeywordMappingBulkFilter): string {
    return JSON.stringify(filter)
}

export function readPageNr(value: unknown): number {
    if (typeof value !== 'number') return 0
    if (Number.isInteger(value) === false) return 0
    if (value < 0) return 0
    return value
}

function readNonNegativeInt(value: unknown): number | null {
    if (typeof value !== 'number') return null
    if (Number.isInteger(value) === false) return null
    if (value < 0) return null
    return value
}

export function readSelectedIds(value: unknown): number[] {
    if (!Array.isArray(value)) return []

    const ids: number[] = []
    for (const item of value) {
        const id = readNonNegativeInt(item)
        if (id === null) continue
        ids.push(id)
    }
    return ids
}

export function readPageIds(value: unknown): number[] {
    return readSelectedIds(value)
}

export function readListCount(value: unknown): number | null {
    return readNonNegativeInt(value)
}

export function isPageFullySelected(
    pageIds: readonly number[],
    selectedIds: readonly number[],
): boolean {
    if (pageIds.length === 0) return false

    const selected = new Set(selectedIds)
    return pageIds.every((id) => selected.has(id))
}

export function isFilterTarget(
    target: KeywordMappingBulkTarget,
): target is KeywordMappingFilterTarget {
    return 'filter' in target
}

export function readBulkPreview(value: unknown): KeywordMappingBulkPreview | null {
    if (!isRecord(value)) return null

    const rowCount = readNonNegativeInt(value.rowCount)
    const searchIntentCount = readNonNegativeInt(value.searchIntentCount)
    const h1RowCount = readNonNegativeInt(value.h1RowCount)
    const outsideTargetCount = readNonNegativeInt(value.outsideTargetCount)
    const maxRows = readNonNegativeInt(value.maxRows)
    if (rowCount === null) return null
    if (searchIntentCount === null) return null
    if (h1RowCount === null) return null
    if (outsideTargetCount === null) return null
    if (maxRows === null) return null

    return { rowCount, searchIntentCount, h1RowCount, outsideTargetCount, maxRows }
}

export function readBulkResultCount(
    value: unknown,
    field: 'updatedRows' | 'deletedRows',
): number | null {
    if (!isRecord(value)) return null
    return readNonNegativeInt(value[field])
}

export function affectedRowCount(
    action: KeywordMappingBulkAction,
    preview: KeywordMappingBulkPreview,
): number {
    if (action === 'h1') return preview.h1RowCount
    return preview.rowCount
}

export function isOverBulkLimit(
    action: KeywordMappingBulkAction,
    preview: KeywordMappingBulkPreview,
): boolean {
    return affectedRowCount(action, preview) > preview.maxRows
}

export function formatKeywordMappingCount(count: number): string {
    return count.toLocaleString('nl-NL')
}

function counted(count: number, singular: string, plural: string): string {
    const formatted = formatKeywordMappingCount(count)
    if (count === 1) return `${formatted} ${singular}`
    return `${formatted} ${plural}`
}

export function h1BulkSentence(preview: KeywordMappingBulkPreview): string {
    const keywords = counted(preview.h1RowCount, 'keyword', 'keywords')
    const intents = counted(preview.searchIntentCount, 'search intent', 'search intents')
    return `This sets the H1 for ${keywords} in ${intents}.`
}

export function groupH1Sentence(keywordCount: number): string {
    return `This sets the H1 for all ${counted(keywordCount, 'keyword', 'keywords')} of this search intent.`
}

export function outsideSelectionMessage(outsideTargetCount: number): string | null {
    if (outsideTargetCount <= 0) return null
    const verb = outsideTargetCount === 1 ? 'is' : 'are'
    return `${formatKeywordMappingCount(outsideTargetCount)} of them ${verb} outside your selection.`
}

export function activeBulkSentence(rowCount: number, active: boolean): string {
    const keywords = counted(rowCount, 'keyword', 'keywords')
    if (active) return `${keywords} will be activated.`
    return `${keywords} will be deactivated.`
}

export function deleteBulkSentence(rowCount: number): string {
    return `${counted(rowCount, 'keyword', 'keywords')} will be deleted.`
}

export function bulkLimitMessage(maxRows: number): string {
    return `This is over the limit of ${formatKeywordMappingCount(maxRows)}. Narrow the filter.`
}

export function bulkModalTitle(action: KeywordMappingBulkAction): string {
    if (action === 'h1') return 'Set H1 text'
    if (action === 'activate') return 'Activate keywords'
    if (action === 'deactivate') return 'Deactivate keywords'
    return 'Delete keywords'
}

export function bulkConfirmLabel(action: KeywordMappingBulkAction, isSaving: boolean): string {
    if (isSaving && action === 'delete') return 'Deleting…'
    if (isSaving) return 'Saving…'
    if (action === 'h1') return 'Set H1 text'
    if (action === 'activate') return 'Activate'
    if (action === 'deactivate') return 'Deactivate'
    return 'Delete'
}

export function bulkFailureMessage(action: KeywordMappingBulkAction): string {
    if (action === 'h1') return 'Could not update the H1 text.'
    if (action === 'activate') return 'Could not activate the keywords.'
    if (action === 'deactivate') return 'Could not deactivate the keywords.'
    return 'Could not delete the keywords.'
}

export function bulkSuccessMessage(action: KeywordMappingBulkAction, count: number | null): string {
    if (count === null) {
        if (action === 'delete') return 'Deleted keywords.'
        if (action === 'activate') return 'Activated keywords.'
        if (action === 'deactivate') return 'Deactivated keywords.'
        return 'Updated keywords.'
    }

    const keywords = counted(count, 'keyword', 'keywords')
    if (action === 'delete') return `Deleted ${keywords}.`
    if (action === 'activate') return `Activated ${keywords}.`
    if (action === 'deactivate') return `Deactivated ${keywords}.`
    return `Updated ${keywords}.`
}

export function confirmSentence(
    action: KeywordMappingBulkAction,
    preview: KeywordMappingBulkPreview,
    groupKeywordCount: number | undefined,
): string {
    if (action === 'h1' && groupKeywordCount !== undefined)
        return groupH1Sentence(groupKeywordCount)
    if (action === 'h1') return h1BulkSentence(preview)
    if (action === 'activate') return activeBulkSentence(preview.rowCount, true)
    if (action === 'deactivate') return activeBulkSentence(preview.rowCount, false)
    return deleteBulkSentence(preview.rowCount)
}

export function deleteCountConfirmed(typed: string, rowCount: number): boolean {
    const compact = typed.trim()
    if (compact === '') return false
    if (compact === String(rowCount)) return true
    return compact === formatKeywordMappingCount(rowCount)
}

export function selectedCountLabel(count: number, matchingFilter: boolean): string {
    const formatted = `${formatKeywordMappingCount(count)} selected`
    if (matchingFilter) return `${formatted} matching the filter`
    return formatted
}

export function buildBulkRequest(
    action: KeywordMappingBulkAction,
    target: KeywordMappingBulkTarget,
    h1Text: string,
    rowCount: number,
): KeywordMappingBulkRequest {
    if (action === 'delete') {
        const body: KeywordMappingBulkWriteBody = { target }
        if (isFilterTarget(target)) body.confirmCount = rowCount
        return { url: KEYWORD_MAPPING_BULK_DELETE_URL, body }
    }
    if (action === 'activate') {
        return { url: KEYWORD_MAPPING_BULK_UPDATE_URL, body: { target, active: true } }
    }
    if (action === 'deactivate') {
        return { url: KEYWORD_MAPPING_BULK_UPDATE_URL, body: { target, active: false } }
    }
    return { url: KEYWORD_MAPPING_BULK_UPDATE_URL, body: { target, h1Text } }
}
